import { useEffect, useRef, useState } from "react";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"] as const;
export type HttpMethod = (typeof METHODS)[number];

interface Props {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

// Custom (non-native) method picker. A button shows the current method
// tinted to match its color; clicking opens a small popover with all
// methods rendered as colored pills. Keyboard: ArrowUp/Down to navigate,
// Enter to pick, Esc to close.
export default function MethodPicker({ value, onChange, className = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<number>(() =>
    Math.max(0, METHODS.indexOf((value as HttpMethod) ?? "GET")),
  );
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => (h + 1) % METHODS.length); }
      if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => (h - 1 + METHODS.length) % METHODS.length); }
      if (e.key === "Enter") {
        e.preventDefault();
        onChange(METHODS[hover]);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, hover, onChange]);

  const m = (value || "GET").toUpperCase();
  return (
    <div className={"relative " + className} ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={"method-pill method-pill-trigger method-pill-" + m.toLowerCase()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`HTTP method: ${m}`}
      >
        <span className="font-mono font-bold tracking-wider">{m}</span>
        <Caret />
      </button>
      {open && (
        <div role="listbox" className="absolute left-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-50 w-32">
          {METHODS.map((opt, i) => (
            <button
              key={opt}
              role="option"
              aria-selected={opt === m}
              onMouseEnter={() => setHover(i)}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={
                "w-full text-left px-2 py-1 rounded flex items-center gap-2 text-sm " +
                (i === hover ? "bg-surface-2" : "")
              }
            >
              <span className={"method-tag " + opt.toLowerCase()}>{opt}</span>
              {opt === m && <span className="ml-auto text-text-faint text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Caret() {
  return (
    <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="ml-1 opacity-70">
      <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
